import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Session persistence key
const SESSION_KEY = 'analytics_session_id';
const SESSION_EXPIRY_KEY = 'analytics_session_expiry';
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

// Get or create session ID (persisted in sessionStorage)
const getOrCreateSessionId = (): string => {
  const existing = sessionStorage.getItem(SESSION_KEY);
  const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
  
  if (existing && expiry && Date.now() < parseInt(expiry)) {
    // Extend session
    sessionStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now() + SESSION_DURATION_MS));
    return existing;
  }
  
  // Create new session
  const newId = crypto.randomUUID();
  sessionStorage.setItem(SESSION_KEY, newId);
  sessionStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now() + SESSION_DURATION_MS));
  return newId;
};

// Get device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  let device_type = 'desktop';
  if (/iPhone|Android.*Mobile|webOS|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    device_type = 'mobile';
  } else if (/iPad|Android/i.test(ua)) {
    device_type = 'tablet';
  }

  let browser = 'Other';
  if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari')) browser = 'Safari';

  let os = 'Other';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';

  return { device_type, browser, os };
};

const getUtmParams = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source'),
    utm_medium: p.get('utm_medium'),
    utm_campaign: p.get('utm_campaign'),
  };
};

// Prevent tracking in preview/development
const isProduction = () => {
  const host = window.location.hostname;
  return host === 'chateaudeclementine.com' || host === 'www.chateaudeclementine.com' || host === 'castel.lovable.app';
};

export const useAnalytics = () => {
  const sessionId = useRef<string>(getOrCreateSessionId());
  const sessionCreated = useRef<boolean>(false);
  const pageStartTime = useRef<number>(Date.now());
  const lastTrackedPath = useRef<string>('');
  const location = useLocation();

  const ensureSession = async () => {
    if (sessionCreated.current || !isProduction()) return;
    
    const deviceInfo = getDeviceInfo();
    const utmParams = getUtmParams();
    
    try {
      // Check if session already exists (page refresh)
      const { data: existing } = await supabase
        .from('analytics_sessions')
        .select('id')
        .eq('session_id', sessionId.current)
        .maybeSingle();

      if (!existing) {
        await supabase.from('analytics_sessions').insert({
          session_id: sessionId.current,
          referrer: document.referrer || null,
          ...utmParams,
          ...deviceInfo,
        });
      }
      
      sessionCreated.current = true;
    } catch (error) {
      console.error('Analytics session error:', error);
    }
  };

  const trackEvent = async (eventType: string, eventName: string, properties?: Record<string, any>) => {
    if (!isProduction()) return;
    
    if (!sessionCreated.current) {
      await ensureSession();
    }

    try {
      await supabase.from('analytics_events').insert({
        session_id: sessionId.current,
        event_type: eventType,
        event_name: eventName,
        page_url: window.location.href,
        page_title: document.title,
        properties: properties || {},
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        ...getUtmParams(),
        ...getDeviceInfo(),
      });
    } catch (error) {
      console.error('Analytics event error:', error);
    }
  };

  const trackPageView = async (pagePath?: string) => {
    const path = pagePath || location.pathname;
    
    // Prevent duplicate tracking for the same path
    if (path === lastTrackedPath.current) return;
    lastTrackedPath.current = path;
    
    const timeOnPrevious = Date.now() - pageStartTime.current;
    await trackEvent('page_view', 'Page View', {
      path,
      title: document.title,
      time_on_previous_page: timeOnPrevious,
    });
    pageStartTime.current = Date.now();
  };

  const trackClick = async (element: string, text?: string, href?: string) => {
    await trackEvent('click', 'Click', { element, text, href });
  };

  const trackFormInteraction = async (formType: string, action: string, field?: string) => {
    await trackEvent('form', 'Form Interaction', { form_type: formType, action, field });
  };

  const trackFormSubmission = async (formType: string, formData: Record<string, any>) => {
    try {
      await trackEvent('form', 'Form Submission', { form_type: formType });

      const utmParams = getUtmParams();
      await supabase.from('form_submissions').insert({
        session_id: sessionId.current,
        form_type: formType,
        form_data: formData,
        page_url: window.location.href,
        referrer: document.referrer || null,
        ...utmParams,
        status: 'submitted',
      });
    } catch (error) {
      console.error('Form submission tracking error:', error);
    }
  };

  const trackScrollDepth = async (depth: number) => {
    await trackEvent('engagement', 'Scroll Depth', { depth_percentage: depth });
  };

  const trackTimeOnPage = async () => {
    const timeSpent = Date.now() - pageStartTime.current;
    await trackEvent('engagement', 'Time on Page', {
      time_spent_ms: timeSpent,
      time_spent_seconds: Math.round(timeSpent / 1000),
    });
  };

  // Initialize session on mount
  useEffect(() => {
    ensureSession();
    // Track initial page view
    trackPageView();

    let maxScrollDepth = 0;
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (documentHeight <= 0) return;
      const scrollDepth = Math.round((scrollTop / documentHeight) * 100);
      
      if (scrollDepth > maxScrollDepth && scrollDepth % 25 === 0) {
        maxScrollDepth = scrollDepth;
        trackScrollDepth(scrollDepth);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Only once on mount

  // Track SPA route changes via react-router
  useEffect(() => {
    // Skip the initial render (handled above)
    if (lastTrackedPath.current === location.pathname) return;
    trackPageView();
  }, [location.pathname]);

  return {
    trackEvent,
    trackPageView,
    trackClick,
    trackFormInteraction,
    trackFormSubmission,
    trackScrollDepth,
    trackTimeOnPage,
    sessionId: sessionId.current,
    isInitialized: true,
  };
};
